<template>
  <div v-if="isDOMLoaded && !isLoadingInMeetingPresence">
    <div :class="{ 'd-none': video }">
      <b-row>
        <b-col>
          <h2>{{ meeting.name }}</h2>
          <b-row>
            <b-col v-if="minuteEventMeetingStart" cols="auto" class="d-flex align-items-center">
              <strong class="mr-1">Date :</strong>
              {{ $moment(toUtc(minuteEventMeetingStart.date)).format("DD/MM/YYYY") }}
            </b-col>
            <!-- NOTE: TASK DEV-4875 -->
            <!-- <b-col v-if="minuteEventMeetingStart" cols="auto" class="d-flex align-items-center">
              <strong class="mr-1">Heure de début :</strong>
              <span
                :id="'minute-event-start'"
                class="cursor-pointer"
                @click="attachTooltip($event, minuteEventMeetingStart)"
              >
                {{ $moment(toUtc(minuteEventMeetingStart.date)).format("HH[h]mm") }}
              </span>
            </b-col> -->

            <b-col v-if="minuteEventMeetingEnd" cols="auto" class="d-flex align-items-center">
              <strong class="mr-1">Heure de fin :</strong>
              <span
                :id="'minute-event-end'"
                class="cursor-pointer"
                @click="attachTooltip($event, minuteEventMeetingEnd)"
              >
                {{ $moment(toUtc(minuteEventMeetingEnd.date)).format("HH[h]mm") }}
              </span>
            </b-col>
            <b-col cols="auto" class="d-flex align-items-center">
              <div v-if="isEventEndDateBeforeStartDate()">
                <font-awesome-icon
                  id="popover-warning-end-date"
                  icon="exclamation-triangle"
                  class="color-red font-16"
                />
                <b-popover target="popover-warning-end-date" triggers="hover" placement="top">
                  Merci de vérifier les heures début et fin, nous avons détecté une incohérence.
                </b-popover>
              </div>
              <div v-if="!areResolutionVoteEventsInOrder()" class="ml-3">
                <font-awesome-icon
                  id="popover-warning-order-res"
                  icon="exclamation-triangle"
                  class="color-yellow font-16"
                />
                <b-popover target="popover-warning-order-res" triggers="hover" placement="top">
                  Merci de vérifier les heures de vote de résolutions, nous avons détecté que
                  l'ordre de vote n'est pas ascendant.
                </b-popover>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <!-- ???: meeting start date + time passed -->
      <hr />
      <b-row>
        <b-col cols="8">
          <b-row>
            <b-col>
              <!-- meeting resolutions (v-for) 1-2-3-4-... -->
              <appResolutionsTunnel
                :meeting-resolutions="parentMeetingResolutions"
                :current-resolution-id="currentResolutionId"
                :current-parent-resolution-id="currentResolution.parent_resolution_id"
                :started-meeting-resolution-id="
                  startedMeetingResolution ? startedMeetingResolution.id : null
                "
                :corrected-meeting-resolution-id="
                  correctedMeetingResolution ? correctedMeetingResolution.id : null
                "
                :meeting-id="meeting.id"
                :building-id="meeting.building_id"
                :meeting-is-completed="meetingIsCompleted"
                @changeResolution="changeResolution($event)"
                @deletedMeetingResolutionDuringMeeting="deleteEventsByResolutionId($event)"
              />
            </b-col>
          </b-row>

          <!-- NOTE: Child Resolution -->
          <div v-if="isChildResolution" class="mb-8">
            <div class="d-flex justify-content-end mb-2">
              <!-- button collapse -->
              <b-button
                class="button btn-blue px-5"
                @click="parentResolutionVisible = !parentResolutionVisible"
              >
                <font-awesome-icon
                  :icon="parentResolutionVisible ? 'chevron-down' : 'chevron-left'"
                />
              </b-button>
            </div>

            <appLockOverlay :condition="blocked && !isCorrectedMeetingResolution">
              <template v-slot:lock-overlay-content>
                <!-- Parent Resolution -->
                <b-collapse
                  id="collapse-parent-resolution"
                  v-model="parentResolutionVisible"
                  visible
                >
                  <b-card class="bg-light mb-4">
                    <appResolution
                      :key="resolutionKey"
                      :is-new="false"
                      :is-in-meeting="true"
                      :is-in-meeting-start="true"
                      :meeting-resolution-id="currentResolution.parent_resolution_id"
                      :meeting-id="meeting.id"
                      :building-id="meeting.building_id"
                      :meeting-is-completed="meetingIsCompleted"
                      :meeting-resolution-status="currentResolution.status"
                      :meeting-resolution-majority-id="currentResolution.majority_id"
                      @resetChildResolution="resetChildResolution"
                      @deleteEventsByResolutionId="deleteEventsByResolutionId"
                      @createResolutionVoteEvent="createResolutionVoteOnMajorChanges"
                    />
                  </b-card>
                </b-collapse>
                <!-- Child resolution -->
                <b-card>
                  <appResolution
                    :key="resolutionKey"
                    :is-new="false"
                    :is-in-meeting="true"
                    :is-in-meeting-start="true"
                    :meeting-parent-resolution-id="currentResolution.parent_resolution_id"
                    :meeting-resolution-id="currentResolutionId"
                    :meeting-id="meeting.id"
                    :building-id="meeting.building_id"
                    :meeting-is-completed="meetingIsCompleted"
                    :meeting-resolution-status="currentResolution.status"
                    :is-in-meeting-start-as-children="true"
                    :meeting-resolution-majority-id="currentResolution.majority_id"
                    @deleteEventsByResolutionId="deleteEventsByResolutionId"
                    @createResolutionVoteEvent="createResolutionVoteOnMajorChanges"
                  />
                </b-card>
              </template>
            </appLockOverlay>
          </div>

          <!-- NOTE: Normal Resolution -->
          <div v-else class="mb-8">
            <appLockOverlay
              :condition="
                blocked && !isCorrectedMeetingResolution && !correctMeetingErrorsIsPressed
              "
            >
              <template v-slot:lock-overlay-content>
                <b-card>
                  <appResolution
                    :key="resolutionKey"
                    :is-new="false"
                    :is-in-meeting="true"
                    :is-in-meeting-start="true"
                    :meeting-resolution-id="currentResolutionId"
                    :meeting-id="meeting.id"
                    :building-id="meeting.building_id"
                    :meeting-is-completed="meetingIsCompleted"
                    :meeting-resolution-status="currentResolution.status"
                    :meeting-resolution-majority-id="currentResolution.majority_id"
                    @deleteEventsByResolutionId="deleteEventsByResolutionId"
                    @createResolutionVoteEvent="createResolutionVoteOnMajorChanges"
                  />
                </b-card>
              </template>
            </appLockOverlay>
          </div>

          <div v-if="!arrivedLeftPressed">
            <!-- Physical votes -->
            <appPhysicalVotesTable
              v-if="
                withVote &&
                mandatesToShowInTable &&
                mandatesToShowInTable.length > 0 &&
                meeting.allowed_modalities.physical
              "
              class="mb-8"
              :mandates="mandatesToShowInTable"
              :current-resolution="currentResolution"
              :show-overlay="
                resolutionInVotingProcess == null &&
                !isStartedMeetingResolution &&
                !isCorrectedMeetingResolution &&
                !correctMeetingErrorsIsPressed
              "
              :is-resolution-blocked="
                meeting.status == meetingStatuses.started
                  ? isBlocked
                  : !correctMeetingErrorsIsPressed
              "
              :meeting-id="meeting.id"
              :minutes="meeting.minutes"
              :show-minutes-data="presenceTableShowMinutesData"
            />

            <!-- Video votes -->
            <appVideoVotes
              v-if="
                withVote &&
                mandatesToShowInVideoTable &&
                mandatesToShowInVideoTable.length > 0 &&
                meeting.allowed_modalities.video
              "
              class="mb-8"
              :mandates="mandatesToShowInVideoTable"
              :current-resolution="currentResolution"
              :show-overlay="
                resolutionInVotingProcess == null &&
                !isStartedMeetingResolution &&
                !isCorrectedMeetingResolution &&
                !correctMeetingErrorsIsPressed
              "
              :meeting-minutes="meeting.minutes"
              :is-resolution-blocked="
                meeting.status == meetingStatuses.started
                  ? isBlocked
                  : !correctMeetingErrorsIsPressed
              "
              :meeting-id="meeting.id"
              :show-minutes-data="presenceTableShowMinutesData"
            />

            <!-- Correspondence votes -->
            <appCorrespondenceVotes
              v-if="
                withVote &&
                correspondenceMandates &&
                correspondenceMandates.length > 0 &&
                meeting.allowed_modalities.correspondence
              "
              class="mb-8"
              :mandates="correspondenceMandates"
              :current-resolution="currentResolution"
              :show-overlay="
                resolutionInVotingProcess == null &&
                !isStartedMeetingResolution &&
                !isCorrectedMeetingResolution &&
                !correctMeetingErrorsIsPressed
              "
              :is-resolution-blocked="
                meeting.status == meetingStatuses.started
                  ? isBlocked
                  : !correctMeetingErrorsIsPressed
              "
              :meeting-id="meeting.id"
              :minutes="meeting.minutes"
              :show-minutes-data="presenceTableShowMinutesData"
            />

            <!-- Ignored correspondence votes -->
            <appCorrespondenceVotes
              v-if="
                withVote &&
                ignoredCorrespondenceMandates &&
                ignoredCorrespondenceMandates.length > 0 &&
                meeting.allowed_modalities.correspondence
              "
              class="mb-8"
              :mandates="ignoredCorrespondenceMandates"
              :current-resolution="currentResolution"
              :show-overlay="!isStartedMeetingResolution && !isCorrectedMeetingResolution"
              :ignored-correspondence="true"
              :meeting-id="meeting.id"
            >
              <template v-slot:recalculate-vote-btn>
                <b-button
                  v-if="showRecalculateIgnoredCorrespondenceBtn"
                  variant="outline-danger"
                  class="mx-3"
                  @click="recalculateCurrentIgnoredCorrespondenceVote"
                >
                  <font-awesome-icon icon="exclamation-circle" />
                  <span class="mx-1">Recalculer avec ces votes</span>
                </b-button>
              </template>
            </appCorrespondenceVotes>

            <!-- Meeting guests -->
            <appMeetingGuests
              v-if="meetingGuests && meetingGuests.length > 0"
              class="mb-8"
              :meeting-guests="meetingGuests"
              :show-overlay="false"
            />
          </div>
        </b-col>

        <b-col cols="4" class="sticky-votings-indicators align-self-start">
          <!-- Indicateurs de Vote -->
          <b-row class="mb-2">
            <b-col>
              <appVotingIndicators
                :with-vote="withVote"
                :meeting="meeting"
                :current-resolution="currentResolution"
                :majority-conditions="majorityConditions"
                :total-present-or-represented-mandates="totalPresentOrRepresentedMandates"
                :total-present-or-represented-tantiemes="totalPresentOrRepresentedTantiemes"
                :total-strict-present-mandates="totalStrictPresentMandates"
                :total-strict-present-tantiemes="totalStrictPresentTantiemes"
                :present-mandates="presentMandates"
                :represented-mandates="representedMandates"
                :video-mandates="videoMandates"
                :represented-video-mandates="representedVideoMandates"
                :correspondence-mandates="correspondenceMandates"
                :absent-mandates-but-voted="absentMandatesButVoted"
                :absent-mandates-but-voted-in-person="absentMandatesButVotedInPerson"
                :video-tantiemes="videoTantiemes"
                :represented-video-tantiemes="representedVideoTantiemes"
                :correspondence-tantiemes="correspondenceTantiemes"
                :absent-but-voted-in-person-tantiemes="absentButVotedInPersonTantiemes"
                :absent-but-voted-tantiemes="absentButVotedTantiemes"
                :has-lack-of-majority="hasLackOfMajority"
                :current-resolution-is-in-correction-mode="isCorrectedMeetingResolution"
                @voteResult="voteResult"
                @revoteRulesResult="revoteRulesResult"
                @changedCurrentResolutionMajorityId="changedCurrentResolutionMajorityId"
              />
            </b-col>
          </b-row>

          <!-- Indicateurs de Présence -->
          <b-row class="mb-2">
            <b-col>
              <appPresenceIndicators
                :meeting="meeting"
                :percent-presence-with-vote-right="percentPresenceWithVoteRight"
                :total-present-or-represented-mandates-with-vote-right="
                  totalPresentOrRepresentedMandatesWithVoteRight
                "
                :total-mandates-with-vote-right="totalMandatesWithVoteRight"
                :present-mandates="presentMandates.length"
                :represented-mandates="representedMandates.length"
                :video-mandates="videoMandates.length"
                :represented-video-mandates="representedVideoMandates.length"
                :correspondence-mandates="correspondenceMandates.length"
                :ignored-correspondence-mandates="ignoredCorrespondenceMandates.length"
                :absent-mandates="absentMandates.length"
                :total-tantiemes="totalTantiemes"
                :total-present-or-represented-tantiemes="totalPresentOrRepresentedTantiemes"
                :all-mandates="allMandates.length"
                :meeting-guests="meetingGuests.length"
                @toggleArrivedLeftPressed="toggleArrivedLeftPressed"
              />
            </b-col>
          </b-row>

          <!-- ACTION BUTTONS -->
          <div v-if="showRecalculateIgnoredCorrespondenceBtn">
            <b-button
              variant="outline-danger"
              class="my-3"
              @click="recalculateAllIgnoredCorrespondenceVotes"
            >
              <font-awesome-icon icon="exclamation-circle" />
              <span class="mx-1">Recalculer tout les votes</span>
            </b-button>
          </div>

          <!-- generate PDFs -->
          <b-row v-if="meetingIsCompleted" class="my-4">
            <b-col cols="auto" class="my-1">
              <b-button variant="light" @click="toggleGeneratePvPressed">
                <font-awesome-icon icon="file-pdf" />
                <span class="mx-1">Procès verbal</span>
              </b-button>
            </b-col>
            <b-col cols="auto" class="my-1">
              <b-button variant="light" @click="GENERATE_PRESENCE_PDF">
                <i class="fa fa-meetup" aria-hidden="true"></i>
                <font-awesome-icon icon="file-pdf" />
                <span class="mx-1">Feuille de présence</span>
              </b-button>
            </b-col>
            <!-- generate EXCEL -->
            <b-col cols="auto" class="my-1">
              <xlsx-workbook v-show="false">
                <xlsx-sheet
                  v-for="sheet in excelSheets"
                  :key="sheet.name"
                  :collection="sheet.data"
                  :sheet-name="sheet.name"
                />
                <xlsx-download :filename="meeting.name + '.xlsx'">
                  <b-button ref="xlsx-download-button" />
                </xlsx-download>
              </xlsx-workbook>
              <b-button variant="outline-success" @click="GENERATE_EXCEL">
                <font-awesome-icon icon="file-excel" />
                <span class="mx-1">Exporter excel</span>
              </b-button>
            </b-col>
          </b-row>

          <!-- end meeting -->
          <b-row v-if="!meetingIsCompleted" class="mb-4">
            <b-col>
              <b-button variant="danger" @click="END_MEETING">
                <font-awesome-icon icon="calendar-check" />
                <span class="mx-1">Fin de la convocation</span>
              </b-button>
            </b-col>
          </b-row>

          <b-row v-if="!meetingIsCompleted" class="my-2">
            <!-- block/unblock -->
            <b-col cols="auto">
              <b-button
                pill
                :variant="blocked ? 'outline-secondary' : 'secondary'"
                @click="toggleIsBlocked"
              >
                <span v-if="blocked">
                  <font-awesome-icon icon="unlock" />
                  <span class="mx-1">Débloquer</span>
                </span>
                <span v-else>
                  <font-awesome-icon icon="lock" />
                  <span class="mx-1">Bloquer</span>
                </span>
              </b-button>
            </b-col>
          </b-row>

          <b-row v-if="votingRulesPass">
            <b-col>
              <!-- set sans vote -->
              <b-row
                v-if="!meetingIsCompleted && !withVote && currentResolution.status == null"
                class="my-2"
              >
                <b-col>
                  <b-button
                    variant="outline-secondary"
                    @click="setResolutionStatus(meetingResolutionStatuses.sans_vote)"
                  >
                    <font-awesome-icon icon="fast-forward" />
                    <span class="mx-1">Clore</span>
                  </b-button>
                </b-col>
              </b-row>

              <b-row v-if="withVote">
                <b-col>
                  <b-row v-if="!meetingIsCompleted" class="my-2">
                    <b-col>
                      <b-row>
                        <b-col>
                          <!-- start vote -->
                          <b-button
                            v-if="
                              !startVoteIsPressed &&
                              startedMeetingResolution == null &&
                              resolutionInVotingProcess == null
                            "
                            variant="outline-success"
                            @click="startVote"
                          >
                            <font-awesome-icon icon="play" />
                            <span class="mx-1">Demarre le vote</span>
                          </b-button>
                          <!-- end vote -->
                          <b-button
                            v-if="isStartedMeetingResolution || resolutionInVotingProcess != null"
                            variant="outline-success"
                            @click="closeResolution"
                          >
                            <font-awesome-icon icon="fast-forward" />
                            <span class="mx-1">Clore</span>
                          </b-button>
                        </b-col>
                      </b-row>
                    </b-col>
                  </b-row>

                  <b-row
                    v-if="
                      correctMeetingErrorsIsPressed ||
                      isStartedMeetingResolution ||
                      resolutionInVotingProcess ||
                      isCorrectedMeetingResolution
                    "
                    class="my-2"
                  >
                    <!-- set not resolved -->
                    <b-col>
                      <b-button
                        variant="outline-secondary"
                        @click="setResolutionStatus(meetingResolutionStatuses.not_resolved)"
                      >
                        <font-awesome-icon icon="hand-rock" />
                        <span class="mx-1">Résolution sans objet</span>
                      </b-button>
                    </b-col>
                  </b-row>

                  <!-- revote -->
                  <b-row v-if="showRevoteBtn" class="my-2">
                    <b-col>
                      <b-button variant="info" @click="revote">
                        <font-awesome-icon icon="redo-alt" />
                        <span class="mx-1">Revoter</span>
                      </b-button>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <!-- automatically vote if only by correspondence -->
              <b-row v-if="isOnlyCorespondance" class="my-2">
                <b-col>
                  <b-button
                    class="text-dark"
                    variant="outline-warning"
                    @click="voteForAllIfByCorrespondance"
                  >
                    <font-awesome-icon icon="envelope" />
                    <span class="mx-1">Votez tous</span>
                  </b-button>
                </b-col>
              </b-row>

              <b-row v-if="meetingIsCompleted" class="my-2">
                <b-col>
                  <!-- start correct vote -->
                  <b-button
                    v-if="!correctMeetingErrorsIsPressed && correctedMeetingResolution == null"
                    variant="outline-danger"
                    @click="startCorrectMeetingResolution"
                  >
                    <font-awesome-icon icon="play" />
                    <span class="mx-1">Corriger la résolution</span>
                    <span>
                      {{ currentResolutionTunnelNumber }}
                    </span>
                  </b-button>
                  <!-- end correct vote -->
                  <b-button
                    v-if="correctMeetingErrorsIsPressed || isCorrectedMeetingResolution"
                    variant="outline-danger"
                    @click="endCorrectMeetingResolution"
                  >
                    <font-awesome-icon icon="fast-forward" />
                    <span class="mx-1">Clore</span>
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row
            v-if="showResetParentResolutionBtn || showResetResolutionBtn || showResetMeetingBtn"
            class="my-4"
          >
            <b-col>
              <!-- reset resolution -->
              <b-row v-if="showResetParentResolutionBtn" class="my-2">
                <b-col>
                  <b-button
                    pill
                    size="sm"
                    variant="outline-danger"
                    @click="resetParentResolutionMainFn"
                  >
                    <font-awesome-icon icon="undo-alt" />
                    <span class="mx-1">Réinitialiser la résolution principale</span>
                  </b-button>
                </b-col>
              </b-row>
              <b-row v-if="showResetResolutionBtn" class="my-2">
                <b-col>
                  <b-button pill variant="outline-danger" @click="resetResolutionMainFn">
                    <font-awesome-icon icon="undo-alt" />
                    <span class="mx-1">Réinitialiser la résolution</span>
                  </b-button>
                  <font-awesome-icon
                    id="popover-reset-resolution"
                    icon="info-circle"
                    size="1x"
                    class="mx-2"
                  />
                  <b-popover target="popover-reset-resolution" triggers="hover" placement="top">
                    Cela réinitialisera la résolution à l'état initial. Il supprimera également tous
                    les votes de résolution. Les votes donnés par correspondance, s'ils existent, ne
                    seront pas supprimés.
                  </b-popover>
                </b-col>
              </b-row>
              <b-row v-if="showResetResolutionBtn" class="my-2">
                <b-col>
                  <label>Garder les votes</label>
                  <b-form-checkbox
                    :value="true"
                    :unchecked-value="false"
                    :checked="keepPhysicalAndVideoVotesOnReset"
                    switch
                    class="d-inline text-center mt-1"
                    @change="keepPhysicalAndVideoVotesOnReset = !keepPhysicalAndVideoVotesOnReset"
                  />
                  <font-awesome-icon
                    id="popover-reset-resolution-keep-votes"
                    icon="info-circle"
                    size="1x"
                    class="mx-1"
                  />
                  <b-popover
                    target="popover-reset-resolution-keep-votes"
                    triggers="hover"
                    placement="top"
                  >
                    Cela empêchera de supprimer les votes de la présence physique et vidéo
                  </b-popover>
                </b-col>
              </b-row>

              <!-- reset meeting -->
              <b-row v-if="showResetMeetingBtn" class="mt-5 mb-2">
                <b-col>
                  <b-button pill size="lg" variant="danger" @click="resetMeeting">
                    <font-awesome-icon icon="exclamation-triangle" />
                    <span class="mx-1">Réinitialiser la réunion</span>
                  </b-button>
                  <font-awesome-icon
                    id="popover-reset-meeting"
                    icon="info-circle"
                    size="1x"
                    class="mx-2"
                  />
                  <b-popover target="popover-reset-meeting" triggers="hover" placement="top">
                    Cela réinitialisera la réunion à l'état initial, en supprimant tous les
                    changements de texte dans les résolutions (à l'exception des fichiers joints).
                    Il supprimera aussi tous les votes, les résultats et la présence. Les votes
                    donnés par la correspondance, s'il existe, ne seront pas supprimés.
                  </b-popover>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <!-- Arrived - Left -->
    <b-modal
      id="modal-arrived-left"
      :static="false"
      :visible="arrivedLeftPressed"
      hide-footer
      no-fade
      @hide="toggleArrivedLeftPressed"
    >
      <appMeetingPresence
        :errors-voting-limit-rules="errorsVotingLimitRules"
        :is-in-meeting-start="true"
        :total-percent-presence="percentPresence"
        :total-tantiemes-charge-general="totalTantiemesChargeGeneral"
        :total-present-or-represented-tantiemes-charge-general="
          totalPresentOrRepresentedTantiemesChargeGeneral
        "
        @loadingEnded="loadingEndedInMeetingPresence"
      />
    </b-modal>

    <Livekit
      v-if="meeting && !meetingIsCompleted && video"
      :meeting-id="meeting.id"
      :livekit-meeting-name="meeting.name + '_' + meeting.id"
    />

    <b-modal
      id="modal-pv-options"
      centered
      :static="false"
      :visible="generatePvPressed"
      hide-footer
      hide-header
      no-fade
      no-close-on-esc
      hide-header-close
      no-close-on-backdrop
    >
      <div class="d-flex flex-column p-3">
        <h5>Voulez-vous inscrire sur le PV:</h5>
        <div class="mt-3 mb-4">
          <div class="d-flex flex-column align-items-start">
            <b-form-checkbox
              v-model="generatePvOption"
              :value="pdfsGeneratePvTypes.generate_type_include_all_participants"
              :unchecked-value="0"
              class="d-inline text-center mb-3"
            >
              l'ensemble des participants
            </b-form-checkbox>
            <b-form-checkbox
              v-model="generatePvOption"
              :value="pdfsGeneratePvTypes.generate_type_include_only_against_and_nsp"
              :unchecked-value="0"
              class="d-inline text-center"
            >
              seulement les opposants et les défaillants
            </b-form-checkbox>
          </div>
        </div>
        <div class="d-flex justify-content-between mt-3">
          <b-button @click="toggleGeneratePvPressed">Annuler</b-button>
          <b-button variant="primary" :disabled="generatePvOption == 0" @click="GENERATE_PV_PDF"
            >Générer le PV</b-button
          >
        </div>
      </div>
    </b-modal>
  </div>
  <appLoader v-else />
</template>

<script>
import MediaMW from "@/middlewares/Media";
import MeetingMW from "@/middlewares/Meeting";
import MandateMW from "@/middlewares/Mandate";
import MeetingResolutionMW from "@/middlewares/MeetingResolution";
import ResolutionVoteMW from "@/middlewares/ResolutionVote";
import ConfigurationMW from "@/middlewares/Configuration";
import PDFMW from "@/middlewares/PDF";
import MeetingCorrespondenceVoteMW from "@/middlewares/MeetingCorrespondenceVote";

import MeetingORM from "@/store/orm-axios/models/Meeting";
import MandateORM from "@/store/orm-axios/models/Mandate";
import MeetingResolutionORM from "@/store/orm-axios/models/MeetingResolution";
import ResolutionVoteORM from "@/store/orm-axios/models/ResolutionVote";
import ConfigurationORM from "@/store/orm-axios/models/Configuration";
import MediaORM from "@/store/orm-axios/models/Media";
import MeetingCorrespondenceVoteORM from "@/store/orm-axios/models/MeetingCorrespondenceVote";

import useAlerts from "@/use/useAlerts.js";
import usePDFs from "@/use/usePDFs.js";

import meetingDetailsMixin from "@/mixins/meeting-details";
import minutesMixin from "@/mixins/minutes";
import correspondenceVotesMixin from "@/mixins/correspondence-votes";

import MeetingGuestMW from "@/middlewares/MeetingGuest";
import MeetingGuestORM from "@/store/orm-axios/models/MeetingGuest";

var checkIfVideoParticipantsArrivedOrLeftInterval;

import { meetingMinutesNames } from "@/constants/meeting.js";

import { XlsxWorkbook, XlsxSheet, XlsxDownload } from "vue-xlsx/dist/vue-xlsx.es.js";

import appLockOverlay from "@/components/snippets/LockOverlay";
import appResolutionsTunnel from "@/components/meetings/meeting-start/ResolutionsTunnel";
import appVotingIndicators from "@/components/meetings/meeting-start/VotingIndicators";
import appPresenceIndicators from "@/components/meetings/meeting-start/PresenceIndicators";
import appResolution from "@/components/catalogs/resolutions/Resolution";
import appMeetingPresence from "./MeetingPresence";
import appPhysicalVotesTable from "@/components/meetings/meeting-start/PhysicalVotesTable.vue";
import appCorrespondenceVotes from "@/components/meetings/meeting-start/CorrespondenceVotesTable.vue";
import appVideoVotes from "@/components/meetings/meeting-start/VideoVotesTable.vue";
import appMeetingManualDatePicker from "@/components/fields/MeetingManualDatePicker";
import appMeetingGuests from "@/components/meetings/meeting-start/MeetingGuestTable.vue";
import AppTooltip from "@/components/snippets/Tooltip";
import Livekit from "./Livekit.vue";
import Vue from "vue";

import { pdfsGeneratePvTypes } from "@/constants/pdf.js";

export default {
  components: {
    appLockOverlay,
    appResolutionsTunnel,
    appVotingIndicators,
    appPresenceIndicators,
    appResolution,
    appMeetingPresence,
    appPhysicalVotesTable,
    appCorrespondenceVotes,
    appVideoVotes,
    appMeetingManualDatePicker,
    appMeetingGuests,
    XlsxWorkbook,
    XlsxSheet,
    XlsxDownload,
    Livekit,
  },
  mixins: [meetingDetailsMixin, minutesMixin, correspondenceVotesMixin],
  props: {
    video: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    // use it to indicate voting limit rules errors
    errorsVotingLimitRules: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  data() {
    return {
      pdfsGeneratePvTypes,
      pdfProp: null,
      isGeneratingPresencePdf: null,
      presenceEvents: null,
      meetingEvents: null,
      isGeneratingPvPdf: false,
      MandateMW: MandateMW,
      MandateORM: MandateORM,
      isLoadingInMeetingPresence: false, // true,
      currentResolutionId: null,
      resolutionKey: 0,
      arrivedLeftPressed: false,
      VOTE_RESULT: false,
      REVOTE_RULES_RESULT: false,
      isBlocked: true,
      correctMeetingErrorsIsPressed: false,
      forceRefetchData: false,
      parentResolutionVisible: true,
      resolutionIsVisible: true,
      presentMandatesFromTable: [],
      pdfFooterMessage: "",
      lastVideoMandatesThatLeft: [],
      lastVideoMandatesThatArrive: [],
      isDOMLoaded: false,
      hasLackOfMajority: false,
      excelSheets: [{ name: "", data: null }],
      generatePvPressed: false,
      generatePvOption: 0,
      resolutionInVotingProcess: null,
      presenceTableShowMinutesData: true,
      keepPhysicalAndVideoVotesOnReset: false,
    };
  },
  computed: {
    meeting() {
      return MeetingORM.getters("getById")(this.$route.params.id);
    },

    showResetMeetingBtn() {
      return !this.checkIfPvSent;
    },
    showResetParentResolutionBtn() {
      if (this.currentResolution.parent_resolution_id) {
        const parentResolution = MeetingResolutionORM.find(
          this.currentResolution.parent_resolution_id
        );

        return !this.meetingIsCompleted && !parentResolution.created_during_meeting;
      }

      return false;
    },
    showResetResolutionBtn() {
      return !this.meetingIsCompleted && !this.currentResolution.created_during_meeting;
    },
    showRevoteBtn() {
      return (
        this.currentResolution.status &&
        this.currentResolution.status !== this.meetingResolutionStatuses.accepted &&
        this.currentResolution.status !== this.meetingResolutionStatuses.not_resolved &&
        this.currentResolution.status !== this.meetingResolutionStatuses.started &&
        this.currentResolution.status !== this.meetingResolutionStatuses.corrected &&
        this.majorityConditions &&
        this.majorityConditions.revote &&
        this.REVOTE_RULES_RESULT &&
        !this.currentResolution.revote_majority_id
      );
    },
    showRecalculateIgnoredCorrespondenceBtn() {
      return (
        !this.startVoteIsPressed &&
        !this.correctVoteIsPressed &&
        !!this.meeting.votes_need_recalculations &&
        this.votingRulesPass
        // ???: .. && !this.meetingIsCompleted
      );
    },

    isOnlyCorespondance() {
      if (!this.meeting.allowed_modalities.correspondence) {
        return false;
      } else {
        if (this.meeting.allowed_modalities.video || this.meeting.allowed_modalities.physical) {
          return false;
        }
      }
      return true;
    },

    blocked() {
      return !!this.isBlocked;
    },

    allMeetingResolutions() {
      return this.meeting
        ? MeetingResolutionORM.getters("getMeetingResolutionsForMeetingStart")(this.meeting.id)
        : [];
    },
    parentMeetingResolutions() {
      return this.$_.filter(this.allMeetingResolutions, resolution => {
        return resolution.parent_resolution_id == null;
      });
    },
    currentResolution() {
      return this.$_.find(this.allMeetingResolutions, resolution => {
        return resolution.id == this.currentResolutionId;
      });
    },
    currentResolutionTunnelNumber() {
      const resolution = this.$_.find(this.allMeetingResolutions, resolution => {
        return resolution.id == this.currentResolution.parent_resolution_id;
      });
      return resolution
        ? resolution.resolution_number + "." + this.currentResolution.resolution_number
        : this.currentResolution.resolution_number;
    },

    startedMeetingResolution() {
      return this.$_.find(this.allMeetingResolutions, resolution => {
        return resolution.status == this.meetingResolutionStatuses.started;
      });
    },
    correctedMeetingResolution() {
      return this.$_.find(this.allMeetingResolutions, resolution => {
        return resolution.status == this.meetingResolutionStatuses.corrected;
      });
    },
    isStartedMeetingResolution() {
      // NOTE: returns true if the active resolution is the started one (active is that one showed on
      // resolution component and the started one is that resolution that has status == started (5) (this can be one at a time))
      if (this.startedMeetingResolution) {
        return this.startedMeetingResolution.id == this.currentResolution.id;
      } else {
        return false;
      }
    },
    isCorrectedMeetingResolution() {
      // NOTE: returns true if the active resolution is the started one (active is that one showed on
      // resolution component and the started one is that resolution that has status == started (5) (this can be one at a time))
      if (this.correctedMeetingResolution) {
        return this.correctedMeetingResolution.id == this.currentResolution.id;
      } else {
        return false;
      }
    },
    startVoteIsPressed() {
      // NOTE: return true if there is a meeting resolution with status == started / else returns false
      return !!this.startedMeetingResolution;
    },
    correctVoteIsPressed() {
      // NOTE: return true if there is a meeting resolution with status == corrected / else returns false
      return !!this.correctedMeetingResolution;
    },

    isParentResolution() {
      return this.currentResolution
        ? this.currentResolution.children_resolutions.length > 0
        : false;
    },
    isChildResolution() {
      return this.currentResolution ? !!this.currentResolution.parent_resolution_id : false;
    },

    allResolutionsAreVoted() {
      const notVoted = this.$_.filter(this.allMeetingResolutions, resolution => {
        return (
          (resolution.status == null ||
            resolution.status == this.meetingResolutionStatuses.started ||
            resolution.status == this.meetingResolutionStatuses.corrected) &&
          resolution.children_resolutions.length == 0
        );
      });

      return this.$_.isEmpty(notVoted);
    },

    allMandates() {
      return MandateORM.getters("getMandatesForMeetingStart")(this.meeting.id);
    },
    // to show in physical votes table
    presentMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return (
          mandate.showed_up == 1 && mandate.presence_status == this.mandatePresenceStatuses.physical
        );
      });
    },
    representedMandates() {
      var representedMandates = this.$_.filter(this.allMandates, mandate => {
        return (
          mandate.showed_up == 1 &&
          mandate.building_owner_id != mandate.representant_owner_id &&
          mandate.presence_status == this.mandatePresenceStatuses.pouvoir
        );
      });
      representedMandates = this.$_.filter(representedMandates, mandate => {
        return !this.checkIfMandateIsAbsent(mandate);
      });
      return representedMandates;
    },
    leftMandates() {
      var leftMandates = this.$_.filter(this.allMandates, mandate => {
        return (
          mandate.showed_up == 1 &&
          mandate.presence_status == this.mandatePresenceStatuses.not_present &&
          mandate.last_presence_status != this.mandatePresenceStatuses.video &&
          mandate.last_presence_status != this.mandatePresenceStatuses.pouvoir_by_video
        );
      });
      this.$_.each(leftMandates, mandate => {
        mandate["$disabled"] = true;
      });
      return leftMandates;
    },
    mandatesToShowInTable() {
      var toShow = [];
      toShow = toShow.concat(this.presentMandates);
      toShow = toShow.concat(this.representedMandates);
      toShow = toShow.concat(this.leftMandates);
      return toShow;
    },

    // to show in video votes table
    videoMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return (
          mandate.showed_up == 1 && mandate.presence_status === this.mandatePresenceStatuses.video
        );
      });
    },
    representedVideoMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return (
          mandate.showed_up == 1 &&
          mandate.building_owner_id != mandate.representant_owner_id &&
          mandate.presence_status === this.mandatePresenceStatuses.pouvoir_by_video
        );
      });
    },
    leftVideoMandates() {
      var leftVideoMandates = this.$_.filter(this.allMandates, mandate => {
        return (
          mandate.showed_up == 1 &&
          mandate.presence_status == this.mandatePresenceStatuses.not_present &&
          (mandate.last_presence_status == this.mandatePresenceStatuses.video ||
            mandate.last_presence_status == this.mandatePresenceStatuses.pouvoir_by_video)
        );
      });
      this.$_.each(leftVideoMandates, mandate => {
        mandate["$disabled"] = true;
      });
      return leftVideoMandates;
    },
    mandatesToShowInVideoTable() {
      var toShow = [];
      toShow = toShow.concat(this.videoMandates);
      toShow = toShow.concat(this.representedVideoMandates);
      toShow = toShow.concat(this.leftVideoMandates);
      return toShow;
    },

    correspondenceMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        var resolutionVote = ResolutionVoteORM.getters("getResolutionVoteByMandateAndResolution")(
          mandate.id,
          this.currentResolutionId
        );
        return (
          mandate.presence_status === this.mandatePresenceStatuses.correspondence &&
          (resolutionVote ? !resolutionVote.ignored_correspondence : true)
        );
      });
    },
    ignoredCorrespondenceMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        var resolutionVote = ResolutionVoteORM.getters("getResolutionVoteByMandateAndResolution")(
          mandate.id,
          this.currentResolutionId
        );

        return (
          mandate.presence_status === this.mandatePresenceStatuses.correspondence &&
          (resolutionVote ? !!resolutionVote.ignored_correspondence : false)
        );
      });
    },
    invalidCorrespondenceMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return mandate.presence_status === this.mandatePresenceStatuses.invalid_correspondence;
      });
    },

    absentMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        return this.checkIfMandateIsAbsent(mandate);
      });
    },
    absentMandatesButVoted() {
      return this.$_.filter(this.absentMandates, mandate => {
        var hasVote = this.$_.find(mandate.resolution_votes, vote => {
          return vote.meeting_has_resolution_id == this.currentResolutionId && vote.vote;
        });
        if (hasVote) return true;
      });
    },
    absentMandatesButVotedInPerson() {
      return this.$_.filter(this.absentMandates, mandate => {
        if (mandate.building_owner_id == mandate.representant_owner_id) {
          var hasVote = this.$_.find(mandate.resolution_votes, vote => {
            return vote.meeting_has_resolution_id == this.currentResolutionId && vote.vote;
          });
          if (hasVote) return true;
        }
      });
    },

    totalStrictPresentMandates() {
      return this.getNumberOfMandates(
        this.presentMandates,
        this.videoMandates,
        this.correspondenceMandates,
        this.absentMandatesButVotedInPerson
      );
    },
    totalPresentOrRepresentedMandates() {
      return this.getNumberOfMandates(
        this.presentMandates,
        this.representedMandates,
        this.videoMandates,
        this.representedVideoMandates,
        this.correspondenceMandates,
        this.absentMandatesButVoted
      );
    },

    totalPresentOrRepresentedMandatesWithVoteRight() {
      let total = 0;
      total += this.countUsedMandatesWithVoteRightByVotingCategory(
        this.presentMandates,
        this.currentResolution.voting_category_id
      );
      total += this.countUsedMandatesWithVoteRightByVotingCategory(
        this.representedMandates,
        this.currentResolution.voting_category_id
      );
      total += this.countUsedMandatesWithVoteRightByVotingCategory(
        this.videoMandates,
        this.currentResolution.voting_category_id
      );
      total += this.countUsedMandatesWithVoteRightByVotingCategory(
        this.representedVideoMandates,
        this.currentResolution.voting_category_id
      );
      total += this.countUsedMandatesWithVoteRightByVotingCategory(
        this.correspondenceMandates,
        this.currentResolution.voting_category_id
      );
      total += this.countUsedMandatesWithVoteRightByVotingCategory(
        this.absentMandatesButVoted,
        this.currentResolution.voting_category_id
      );
      return total;
    },

    totalMandatesWithVoteRight() {
      let total = 0;
      total += this.countUsedMandatesWithVoteRightByVotingCategory(
        this.allMandates,
        this.currentResolution.voting_category_id
      );
      return total;
    },

    totalTantiemes() {
      return this.getUsedTantiemesByVotingCategory(this.currentResolution.voting_category_id);
    },
    totalTantiemesChargeGeneral() {
      return this.getUsedTantiemesChargeGeneral();
    },
    totalStrictPresentTantiemes() {
      return this.getSumOfTantiemes(
        this.getUsedMandatesTantiemesByVotingCategory(
          this.presentMandates,
          this.currentResolution.voting_category_id
        ),
        this.videoTantiemes,
        this.correspondenceTantiemes,
        this.absentButVotedInPersonTantiemes
      );
    },
    totalPresentOrRepresentedTantiemes() {
      return this.getSumOfTantiemes(
        this.getUsedMandatesTantiemesByVotingCategory(
          this.presentMandates,
          this.currentResolution.voting_category_id
        ),
        this.representedTantiemes,
        this.videoTantiemes,
        this.representedVideoTantiemes,
        this.correspondenceTantiemes,
        this.absentButVotedTantiemes
      );
    },
    totalPresentOrRepresentedTantiemesChargeGeneral() {
      return this.getSumOfTantiemes(
        this.getUsedMandatesTantiemesByChargeGeneral(this.presentMandates),
        this.getUsedMandatesTantiemesByChargeGeneral(this.representedMandates),
        this.getUsedMandatesTantiemesByChargeGeneral(this.videoMandates),
        this.getUsedMandatesTantiemesByChargeGeneral(this.representedVideoMandates),
        this.getUsedMandatesTantiemesByChargeGeneral(this.correspondenceMandates),
        this.getUsedMandatesTantiemesByChargeGeneral(this.absentMandatesButVoted)
      );
    },
    representedTantiemes() {
      return this.getUsedMandatesTantiemesByVotingCategory(
        this.representedMandates,
        this.currentResolution.voting_category_id
      );
    },
    videoTantiemes() {
      return this.getUsedMandatesTantiemesByVotingCategory(
        this.videoMandates,
        this.currentResolution.voting_category_id
      );
    },
    representedVideoTantiemes() {
      return this.getUsedMandatesTantiemesByVotingCategory(
        this.representedVideoMandates,
        this.currentResolution.voting_category_id
      );
    },
    correspondenceTantiemes() {
      return this.getUsedMandatesTantiemesByVotingCategory(
        this.correspondenceMandates,
        this.currentResolution.voting_category_id
      );
    },
    absentButVotedTantiemes() {
      return this.getUsedMandatesTantiemesByVotingCategory(
        this.absentMandatesButVoted,
        this.currentResolution.voting_category_id
      );
    },
    absentButVotedInPersonTantiemes() {
      return this.getUsedMandatesTantiemesByVotingCategory(
        this.absentMandatesButVotedInPerson,
        this.currentResolution.voting_category_id
      );
    },

    percentPresence() {
      var total = this.allMandates.length;
      // TODO: we need to add here the phone & physical_tab
      var totalPresent =
        this.presentMandates.length +
        this.representedMandates.length +
        this.videoMandates.length +
        this.representedVideoMandates.length +
        this.correspondenceMandates.length;

      var percentPresence = (totalPresent * 100) / total;
      return this.formatNumber(percentPresence);
    },

    percentPresenceWithVoteRight() {
      var percentPresence =
        (this.totalPresentOrRepresentedMandatesWithVoteRight * 100) /
        this.totalMandatesWithVoteRight;
      return this.formatNumber(percentPresence);
    },

    // !!!: ???: TODO: reset resolution when setting majority null
    withVote() {
      var currentMajority = this.findMajorityData(this.currentResolution.majority_id);
      try {
        if (currentMajority.majority_conditions && !this.isParentResolution) {
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    votingRulesPass() {
      return this.errorsVotingLimitRules.length == 0;
    },
    currentMajority() {
      try {
        var majorityId = null;
        if (this.currentResolution.revote_majority_id) {
          majorityId = this.currentResolution.revote_majority_id;
        } else {
          majorityId = this.currentResolution.majority_id;
        }
        return this.findMajorityData(majorityId);
      } catch {
        return {};
      }
    },
    majorityConditions() {
      try {
        return this.currentMajority.majority_conditions;
      } catch {
        return {};
      }
    },

    minuteEventMeetingStart() {
      return this.$_.find(this.meeting.minutes, minute => {
        return minute.event == meetingMinutesNames.meeting_start;
      });
    },
    minuteEventMeetingEnd() {
      return this.$_.find(this.meeting.minutes, minute => {
        return minute.event == "meeting-end";
      });
    },

    meetingIsCompleted() {
      if (
        [
          this.meetingStatuses.completed,
          this.meetingStatuses.pvReadyToBeSent,
          this.meetingStatuses.pvInProgress,
          this.meetingStatuses.pvSent, // ..
        ].includes(this.meeting.status)
      ) {
        return true;
      }
      return false;
    },

    meetingComputed() {
      return MeetingORM.find(this.$route.params.id);
    },
    configuration() {
      // There will be only one configuration for each agency id
      return ConfigurationORM.query().where("agency_id", this.agencyId).first();
    },
    timezone() {
      return localStorage.getItem("timezone");
    },
    meetingGuests() {
      return MeetingGuestORM.query().where("meeting_id", this.meeting.id).get();
    },

    checkIfPvSent() {
      return (
        this.meeting.status == this.meetingStatuses.pvReadyToBeSent ||
        this.meeting.status == this.meetingStatuses.pvInProgress ||
        this.meeting.status == this.meetingStatuses.pvSent
      );
    },
    mediaSignatures() {
      return MediaORM.getters("getAll");
    },
  },
  watch: {
    async currentResolutionId(val) {
      try {
        this.$store.state.showLoaderOverlay = true;
        this.resolutionIsVisible = true;
        this.isBlocked = true;
        this.resolutionInVotingProcess = null;
        // HACK: ..
        if (!this.isParentResolution) await this.updateMeetingCurrentResolutionId(val);
        this.forceRerenderResolution();
        this.$store.state.showLoaderOverlay = false;
      } catch {
        this.$store.state.showLoaderOverlay = false;
      }
    },
    isParentResolution(val) {
      if (val) {
        this.parentResolutionVisible = true;
        this.currentResolutionId = this.currentResolution.children_resolutions[0].id;
      }
    },
    configuration() {
      this.getPdfConfigs();
    },
    // HACK: Sometimes contactsData or majoritiesData is missing
    async meeting(newVal, oldVal) {
      if (newVal.freezed_meeting_details && oldVal.freezed_meeting_details) {
        if (
          newVal.freezed_meeting_details.ContactsData &&
          oldVal.freezed_meeting_details.ContactsData &&
          newVal.freezed_meeting_details.ContactsData.length >
            oldVal.freezed_meeting_details.ContactsData.length
        ) {
          this.setContactsData(this.meeting.building_id);
        }
        if (
          newVal.freezed_meeting_details.MajoritiesData &&
          newVal.freezed_meeting_details.MajoritiesData.length == 0
        ) {
          this.setMajoritiesData(this.meeting.building_id);
          this.setFreezedMeetingDetailsInMeeting();
        }
      }
    },
  },
  async created() {
    this.currentResolutionId =
      this.parentMeetingResolutions.length !== 0 ? this.parentMeetingResolutions[0].id : null;

    ConfigurationMW.FETCH(this.agencyId).then(() => {
      this.getPdfConfigs();
    });

    await MeetingGuestMW.FETCH(this.meeting.id);

    if (this.timezone) {
      this.$moment.tz.setDefault(this.timezone);
    }
    // NOTE: Set default
    this.generatePvOption = this.pdfsGeneratePvTypes.generate_type_include_all_participants;
  },
  mounted() {
    if (this.meeting) {
      if (this.meeting.allowed_modalities.video) {
        this.checkIfVideoParticipantsArrivedOrLeft(this.meeting);
        checkIfVideoParticipantsArrivedOrLeftInterval = setInterval(() => {
          this.checkIfVideoParticipantsArrivedOrLeft(this.meeting);
        }, process.env.VUE_APP_CHECK_LAST_SEEN_INTERVAL);
      }
    }
    this.isDOMLoaded = true;
  },
  beforeDestroy() {
    clearInterval(checkIfVideoParticipantsArrivedOrLeftInterval);
  },
  methods: {
    resetChildResolution() {
      if (this.isChildResolution) {
        this.forceRerenderResolution();
      }
    },
    loadingEndedInMeetingPresence() {
      this.isLoadingInMeetingPresence = false;
    },
    async startVote() {
      let currentResolution = MeetingResolutionORM.find(this.currentResolutionId);

      // NOTE: Show warning if is voted after was already voted
      if (
        this.hasMinutesRegisteredChangesOnMandatesAfterCurrentResolution(this.currentResolutionId)
      ) {
        // TODO: ???: Is the question text ok ???
        // Changes were made to the meeting after the vote of this resolution. This changes will affect the resolution votes result, do you want to continue with the new changes ?
        const questionResult = await this.$swal(
          this.alertQuestion(
            "Attention",
            "Des modifications ont été apportées à l'assemblée après le vote de cette résolution. Ces changements affecteront le résultat des votes de résolution, voulez-vous continuer avec les nouveaux changements ?"
          )
        );
        this.presenceTableShowMinutesData = false;
        // NOTE: If false -> continue to vote with current mandates that voted before (are showed from minutes)
        // Also, keep the resolution_vote event date the same
        if (!questionResult.value) {
          this.presenceTableShowMinutesData = true;
        }
      }

      if (currentResolution) {
        this.resolutionInVotingProcess = currentResolution;
        currentResolution.status = this.meetingResolutionStatuses.started;
        await MeetingResolutionMW.PUT(currentResolution).catch(err => {
          console.error(err);
        });
      } else {
        console.warn("MResolution was not found");
      }
    },
    changeResolution(resolutionId) {
      ResolutionVoteORM.deleteAll();
      this.currentResolutionId = resolutionId;
      // NOTE: By default, data shown in presenceTables will be from minutes if exists
      this.presenceTableShowMinutesData = true;
    },
    async updateMeetingCurrentResolutionId(resolutionId) {
      this.correctMeetingErrorsIsPressed = false;
      return new Promise((resolve, reject) => {
        MeetingORM.update({
          where: this.meeting.id,
          data: { current_resolution_id: resolutionId },
        });

        // NOTE: Update only current_resolution_id
        MeetingMW.UPDATE_CURRENT_RESOLUTION(
          {
            id: this.meeting.id,
            current_resolution_id: resolutionId,
          },
          false
        )
          .then(res => {
            resolve(res);
          })
          .catch(err => {
            console.log("err update current_resolution_id", err);
            reject(err);
          });
      });
    },
    toggleArrivedLeftPressed() {
      this.arrivedLeftPressed = !this.arrivedLeftPressed;
    },
    toggleGeneratePvPressed() {
      this.generatePvOption = this.pdfsGeneratePvTypes.generate_type_include_all_participants;
      this.generatePvPressed = !this.generatePvPressed;
    },
    voteResult(result, hasLackOfMajority) {
      this.VOTE_RESULT = result;
      this.hasLackOfMajority = hasLackOfMajority;
    },
    revoteRulesResult(result) {
      this.REVOTE_RULES_RESULT = result;
    },

    async resetResolutionMainFn() {
      try {
        const questionResult = await this.$swal(this.alertQuestion());

        if (questionResult.value) {
          this.$store.state.showLoaderOverlay = true;

          if (this.isOnlyCorespondance) {
            await this.resetResolution();
          } else {
            await this.deleteEventsByResolutionId(this.currentResolution.id);

            // NOTE: Keep resolutionVotes for physical and video presence DEV-4786
            if (!this.keepPhysicalAndVideoVotesOnReset) {
              await this.resetResolutionVotes();
            }

            await this.resetResolution();
          }

          this.$store.state.showLoaderOverlay = false;
        }
      } catch (err) {
        this.$store.state.showLoaderOverlay = false;
        console.error(err);
        this.$swal(this.alertError());
      }
    },
    async resetParentResolutionMainFn() {
      try {
        const questionResult = await this.$swal(this.alertQuestion());

        if (questionResult.value) {
          this.$store.state.showLoaderOverlay = true;

          await this.resetParentResolution();

          this.$store.state.showLoaderOverlay = false;
        }
      } catch (err) {
        this.$store.state.showLoaderOverlay = false;
        console.error(err);
        this.$swal(this.alertError());
      }
    },
    async deleteEventsByResolutionId(resolutionId) {
      try {
        var self = this;
        var p = [];
        self.$_.each(self.meeting.minutes, minute => {
          if (minute.resolution_id != resolutionId) {
            p.push(minute);
          }
        });
        self.meeting.minutes = p;
        await MeetingORM.update({
          where: self.meeting.id,
          data: {
            minutes: self.meeting.minutes,
          },
        }).then(async updatedMeeting => {
          await MeetingMW.PUT(updatedMeeting, false).then(res => {
            return Promise.resolve(res);
          });
        });
      } catch (err) {
        self.$swal(self.alertError());
        return Promise.reject(err);
      }
    },
    async resetResolutionVotes() {
      const voteType = null;
      let mandatesThatNeedToVote = [];

      const meetingResolution = MeetingResolutionORM.find(this.currentResolutionId);
      const presentOrRepresentedMandates = [].concat(
        this.presentMandates,
        this.representedMandates
      );

      presentOrRepresentedMandates.forEach(mandate => {
        if (
          this.getUsedMandateTantiemesByVotingCategory(
            mandate.mandate_id ? mandate.mandate_id : mandate.id, // ???: because of confusing notation in minutes
            meetingResolution.voting_category_id
          ) != 0
        ) {
          mandatesThatNeedToVote.push(mandate);
        }
      });

      if (mandatesThatNeedToVote.length == 0) return;

      const bulkVotePostObj = this.prepareBulkVotePostObj(
        voteType,
        mandatesThatNeedToVote,
        this.currentResolutionId
      );
      await ResolutionVoteMW.BULK_VOTE(bulkVotePostObj);
    },
    async resetResolution() {
      await MeetingResolutionMW.RESET(this.currentResolution).then(() => {
        this.forceRerenderResolution();
      });
    },
    async resetParentResolution() {
      if (this.currentResolution.parent_resolution_id) {
        const parentResolution = MeetingResolutionORM.find(
          this.currentResolution.parent_resolution_id
        );
        await MeetingResolutionMW.RESET(parentResolution).then(() => {
          this.forceRerenderResolution();
        });
      }
    },

    async closeResolution() {
      this.resolutionInVotingProcess = null;
      // !!!: If minutes "meeting-start" is missing, create it when start voting on a resolution + save freezed meetingDetails in DB
      if (!this.minuteEventMeetingStart) {
        try {
          await this.setCorrespondenceEvents();
          await this.setGeneralDetailsAndMeetingStartEvents();
          await this.setFreezedMeetingDetailsInMeeting(true);
        } catch (err) {
          this.$swal(this.alertError());
          throw new Error(err);
        }
      }

      var resolutionVotes = ResolutionVoteORM.query()
        .where("meeting_has_resolution_id", this.currentResolutionId)
        .where(vote => {
          // !!!: ignored_correspondence + invalid_correspondence
          return (
            vote.vote !== null &&
            !vote.ignored_correspondence &&
            vote.presence_status != this.mandatePresenceStatuses.invalid_correspondence
          );
        })
        .get();

      if (this.totalPresentOrRepresentedMandates == resolutionVotes.length) {
        this.updateResolutionOnClose();
      } else {
        var self = this;
        self
          .$swal(self.alertQuestion())
          .then(async res => {
            if (res.value) {
              // NOTE: insert or update votes abstain functionality ..
              this.$store.state.showLoaderOverlay = true;
              await this.abstainFunctionalityForPhysicalPresence(this.currentResolutionId);
              await this.nspFunctionalityForVideoPresence(this.currentResolutionId);
              await this.nspFunctionalityForCorrespondence(this.currentResolutionId);
              this.updateResolutionOnClose();
            }
          })
          .catch(err => {
            console.log("err", err);
            this.$store.state.showLoaderOverlay = false;
            self.$swal(self.alertError());
          });
      }
    },
    async abstainFunctionalityForPhysicalPresence(currentResolutionId) {
      let voteType = this.voteTypes.abstain;

      let mandates = this.getMandatesUpdatedFromMinutes();
      let resolutionVotes = ResolutionVoteORM.query().get();

      let presentOrRepresentedMandatesThatNeedToVote = this.$_.filter(mandates, mandate => {
        let voted = this.$_.find(resolutionVotes, vote => {
          return (
            vote.mandate_id == mandate.id && vote.meeting_has_resolution_id == currentResolutionId
          );
        });
        var votedButNull = this.$_.find(resolutionVotes, vote => {
          return (
            vote.mandate_id == mandate.id &&
            vote.meeting_has_resolution_id == currentResolutionId &&
            vote.vote == null
          );
        });
        return (
          (mandate.presence_status == this.mandatePresenceStatuses.physical ||
            mandate.presence_status == this.mandatePresenceStatuses.pouvoir) &&
          (!voted || votedButNull)
        );
      });

      let mandatesThatNeedToVote = [];

      let meetingResolution = MeetingResolutionORM.find(currentResolutionId);
      presentOrRepresentedMandatesThatNeedToVote.forEach(mandate => {
        if (
          this.getUsedMandateTantiemesByVotingCategory(
            mandate.mandate_id ? mandate.mandate_id : mandate.id, // ???: because of confusing notation in minutes
            meetingResolution.voting_category_id
          ) != 0
        ) {
          mandatesThatNeedToVote.push(mandate);
        }
      });

      // prepare post object for bulk vote
      var bulkVotePostObj = this.prepareBulkVotePostObj(
        voteType,
        mandatesThatNeedToVote,
        currentResolutionId
      );

      if (
        bulkVotePostObj.mandates_ids.length == 0 ||
        bulkVotePostObj.meeting_resolution_id == null ||
        bulkVotePostObj.vote_type == null
      ) {
        this.forceRefetchData = true;
        this.$store.state.showLoaderOverlay = false;
        return;
      }

      const currentMajority = this.findMajorityData(this.currentResolution.majority_id);
      if (currentMajority && currentMajority.majority_conditions) {
        await ResolutionVoteMW.BULK_VOTE(bulkVotePostObj)
          .then(res => {
            this.forceRefetchData = true;
            this.$store.state.showLoaderOverlay = false;
          })
          .catch(err => {
            console.log("err set votes abstain", err);
            this.$store.state.showLoaderOverlay = false;
            this.$swal(this.alertError());
          });
      } else {
        this.forceRefetchData = true;
        this.$store.state.showLoaderOverlay = false;
      }
    },
    async nspFunctionalityForVideoPresence(currentResolutionId) {
      let videoVoteType = this.voteTypes.nsp;

      let mandatesThatNeedToVoteVideo = [];

      let mandates = this.getMandatesUpdatedFromMinutes();
      let resolutionVotes = ResolutionVoteORM.query().get();
      let meetingResolution = MeetingResolutionORM.find(currentResolutionId);

      let videoMandatesThatNeedToVote = this.$_.filter(mandates, mandate => {
        var voted = this.$_.find(resolutionVotes, vote => {
          return (
            vote.mandate_id == mandate.id && vote.meeting_has_resolution_id == currentResolutionId
          );
        });
        var votedButNull = this.$_.find(resolutionVotes, vote => {
          return (
            vote.mandate_id == mandate.id &&
            vote.meeting_has_resolution_id == currentResolutionId &&
            vote.vote == null
          );
        });

        return (
          (mandate.presence_status == this.mandatePresenceStatuses.video ||
            mandate.presence_status == this.mandatePresenceStatuses.pouvoir_by_video) &&
          (!voted || votedButNull)
        );
      });

      videoMandatesThatNeedToVote.forEach(mandate => {
        if (
          this.getUsedMandateTantiemesByVotingCategory(
            mandate.mandate_id ? mandate.mandate_id : mandate.id, // ???: because of confusing notation in minutes
            meetingResolution.voting_category_id
          ) != 0
        ) {
          mandatesThatNeedToVoteVideo.push(mandate);
        }
      });

      let bulkVotePostObj = this.prepareBulkVotePostObj(
        videoVoteType,
        mandatesThatNeedToVoteVideo,
        currentResolutionId
      );

      if (
        bulkVotePostObj.mandates_ids.length == 0 ||
        bulkVotePostObj.meeting_resolution_id == null ||
        bulkVotePostObj.vote_type == null
      ) {
        this.forceRefetchData = true;
        this.$store.state.showLoaderOverlay = false;
        return;
      }

      const currentMajority = this.findMajorityData(this.currentResolution.majority_id);
      if (currentMajority && currentMajority.majority_conditions) {
        await ResolutionVoteMW.BULK_VOTE(bulkVotePostObj)
          .then(res => {
            this.forceRefetchData = true;
            this.$store.state.showLoaderOverlay = false;
          })
          .catch(err => {
            console.log("err set votes nsp", err);
            this.$store.state.showLoaderOverlay = false;
            this.$swal(this.alertError());
          });
      } else {
        this.forceRefetchData = true;
        this.$store.state.showLoaderOverlay = false;
      }
    },
    async nspFunctionalityForCorrespondence(meetingResolutionId) {
      let meetingCorrespondenceVotes = MeetingCorrespondenceVoteORM.query()
        .where("meeting_id", this.meeting.id)
        .get();

      let meetingResolution = MeetingResolutionORM.find(meetingResolutionId);

      meetingCorrespondenceVotes.forEach(meetingCorrespondenceVote => {
        let mandate = MandateORM.getters("getById")(meetingCorrespondenceVote.mandate_id);
        // NOTE: Check all mandates to have more then 0 tantiemes
        if (
          this.getUsedMandateTantiemesByVotingCategory(
            mandate.id,
            meetingResolution.voting_category_id
          ) != 0
        ) {
          let proposed_votes = meetingCorrespondenceVote.proposed_votes;
          if (proposed_votes && proposed_votes.length > 0) {
            // NOTE: Look for propose vote to exist
            let proposedVoteIndex = this.$_.findIndex(proposed_votes, item => {
              return (
                item.meeting_resolution_id == meetingResolutionId &&
                item.majority_id == meetingResolution.majority_id
              );
            });
            if (proposedVoteIndex == "-1") {
              // NOTE: Create new propose vote
              proposed_votes.push({
                majority_id: meetingResolution.majority_id,
                meeting_resolution_id: meetingResolutionId,
                vote: this.voteTypes.nsp,
              });
              MeetingCorrespondenceVoteORM.update({
                where: meetingCorrespondenceVote.id,
                data: {
                  proposed_votes: proposed_votes,
                },
              });
              MeetingCorrespondenceVoteMW.PUT(meetingCorrespondenceVote);

              // NOTE: Create new resolutionVote, because when correspondence mandate comes with NULL vote, resolutionVote doesn't exist
              let existentVote = ResolutionVoteORM.getters(
                "getResolutionVoteByMandateAndResolution"
              )(mandate.id, meetingResolutionId);

              if (!existentVote) {
                ResolutionVoteORM.insertOrUpdate({
                  data: {
                    meeting_has_resolution_id: meetingResolutionId,
                    mandate_id: mandate.id,
                    vote: null,
                    presence_status: mandate.presence_status,
                    agency_id: this.agencyId,
                  },
                }).then(async res => {
                  ResolutionVoteMW.POST(res.resolution_vote[0]);
                });
              }
            }
          }
        }
      });
    },
    updateResolutionOnClose() {
      var status = null;

      if (!this.VOTE_RESULT && this.hasLackOfMajority) {
        status = this.meetingResolutionStatuses.rejected_lack_of_majority;
      } else if (!this.VOTE_RESULT) {
        status = this.meetingResolutionStatuses.rejected;
      } else {
        status = this.meetingResolutionStatuses.accepted;
      }

      if (!this.withVote) {
        status = this.meetingResolutionStatuses.sans_vote;
      }

      this.setResolutionStatus(status);
    },
    async setResolutionStatus(status) {
      this.correctMeetingErrorsIsPressed = false;
      this.resolutionInVotingProcess = null;
      this.$store.state.showLoaderOverlay = true;

      MeetingResolutionORM.update({
        where: this.currentResolutionId,
        data: { status: status },
      });

      // NOTE: Make sure to set initial majority (revote majority) before MeetingResolution UPDATE (in case of revote)
      this.setInitialMeetingResolutionBeforeUpdate();
      // update DB value also
      const currentResolution = MeetingResolutionORM.find(this.currentResolutionId);
      MeetingResolutionMW.PUT(currentResolution)
        .then(async () => {
          this.isBlocked = true;

          // NOTE: Display desired first majority (in case was choosed by user)
          this.setDesiredMeetingResolutionAfterUpdate();

          // NOTE: Update resolution_vote event when presenceTableShowMinutesData (keep the resolution_vote event Date the same)
          let updateVotedResolution = this.meetingIsCompleted || this.presenceTableShowMinutesData;
          await this.setResolutionVoteEvent(
            this.currentResolution.id,
            this.currentMajority.id,
            true,
            updateVotedResolution,
            !!this.currentResolution.old_revote_majority_id
          );

          this.$store.state.showLoaderOverlay = false;
        })
        .catch(err => {
          console.error(err);
          this.$swal(
            this.alertError("Une erreur s'est produite sur le processus de vote", "", 3000)
          );
          // NOTE: In case of error, set meetingResolution status to NULL
          this.setMeetingResolutionStatusNull(this.currentResolutionId);

          // NOTE: Display desired first majority (in case was choosed by user)
          this.setDesiredMeetingResolutionAfterUpdate();

          this.$store.state.showLoaderOverlay = false;
        });
    },
    // force refetch data in dyn table
    stopForceRefetchData() {
      this.forceRefetchData = false;
    },
    toggleIsBlocked() {
      this.isBlocked = !this.isBlocked;
    },

    async GENERATE_PRESENCE_PDF() {
      // check if the Agency has a logo
      if (localStorage.getItem("userMediaId")) {
        this.$store.state.showLoaderOverlay = true;
        let meeting = MeetingORM.query()
          .with("meeting_resolutions.children_resolutions")
          .where("id", this.$route.params.id)
          .first();

        await MediaMW.MEDIA_GET_SIGNATURES(meeting.id);

        const idLogoAgency = localStorage.getItem("userMediaId");
        MediaMW.GET_LOGO_IN_BASE64(idLogoAgency)
          .then(async logo => {
            meeting["logoInBase64"] = logo["file_as_base64_string"];

            const { generateTemplatePresence, downloadDialog } = usePDFs();
            generateTemplatePresence(meeting, this.pdfFooterMessage, this.mediaSignatures).then(
              template => {
                PDFMW.MEETING_PDF_CREATE_SAVE_RETURN(this.$route.params.id, {
                  presence: template,
                }).then(presencePDF => {
                  const meetingStartDate = this.$moment(meeting.meeting_date).format("YYYY-MM-DD");
                  downloadDialog(
                    presencePDF.response.data.document,
                    meetingStartDate + " " + meeting.name + " Feuille de présence",
                    "pdf"
                  );
                  this.$store.state.showLoaderOverlay = false;
                });
              }
            );
          })
          .catch(err => {
            this.$store.state.showLoaderOverlay = false;
            const { alertErrorAndReport } = useAlerts();
            alertErrorAndReport(
              err,
              "Impossible d'extraire le logo !",
              "Veuillez vérifier votre connexion Internet."
            );
          });
      } else {
        this.$swal({
          icon: "warning",
          title: "Vous n'avez spécifié aucun logo pour votre agence !",
          html: "Accédez au profil et ajoutez un logo d'agence.",
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "Ajouter le logo",
          confirmButtonClass: "btn btn-primary btn-lg",
          cancelButtonText: "Pas maintenant",
        }).then(res => {
          if (res.value) {
            this.$router.push({
              name: "profile",
            });
          }
        });
      }
    },
    GENERATE_PV_PDF() {
      this.generatePvPressed = false;
      // check if the Agency has a logo
      if (localStorage.getItem("userMediaId")) {
        this.$store.state.showLoaderOverlay = true;
        let meeting = MeetingORM.query()
          .with("meeting_resolutions.children_resolutions")
          .where("id", this.$route.params.id)
          .first();

        const idLogoAgency = localStorage.getItem("userMediaId");
        MediaMW.GET_LOGO_IN_BASE64(idLogoAgency)
          .then(async logo => {
            meeting["logoInBase64"] = logo["file_as_base64_string"];

            const { generateTemplatePv, downloadDialog } = usePDFs();
            generateTemplatePv(meeting, this.pdfFooterMessage, this.generatePvOption).then(
              template => {
                PDFMW.MEETING_PDF_CREATE_SAVE_RETURN(this.$route.params.id, {
                  pv: template,
                }).then(pvPDF => {
                  const meetingStartDate = this.$moment(meeting.meeting_date).format("YYYY-MM-DD");
                  downloadDialog(
                    pvPDF.response.data.document,
                    meetingStartDate + " " + meeting.name + " Procès verbal",
                    "pdf"
                  );
                  this.$store.state.showLoaderOverlay = false;
                });
              }
            );
          })
          .catch(err => {
            this.$store.state.showLoaderOverlay = false;
            const { alertErrorAndReport } = useAlerts();
            alertErrorAndReport(
              err,
              "Impossible d'extraire le logo !",
              "Veuillez vérifier votre connexion Internet."
            );
          });
      } else {
        this.$swal({
          icon: "warning",
          title: "Vous n'avez spécifié aucun logo pour votre agence !",
          html: "Accédez au profil et ajoutez un logo d'agence.",
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "Ajouter le logo",
          confirmButtonClass: "btn btn-primary btn-lg",
          cancelButtonText: "Pas maintenant",
        }).then(res => {
          if (res.value) {
            this.$router.push({
              name: "profile",
            });
          }
        });
      }
    },

    END_MEETING() {
      var self = this;
      self
        .$swal(self.alertQuestion())
        .then(async res => {
          if (res.value) {
            this.$store.state.showLoaderOverlay = true;

            // Create meeting-start event if is not created
            if (!this.minuteEventMeetingStart) {
              try {
                await this.setCorrespondenceEvents();
                await this.setGeneralDetailsAndMeetingStartEvents();
                await this.setFreezedMeetingDetailsInMeeting(true);
              } catch {
                this.$swal(this.alertError());
                throw new Error(err);
              }
            }

            // Set not voted on resolutions without votes(status = null)
            if (!this.allResolutionsAreVoted) {
              await this.doVoteAllNotVoted();
              await MeetingMW.MEETING_RESOLUTION_STATUS_NOT_VOTED(this.meeting).catch(err => {
                this.$store.state.showLoaderOverlay = false;
              });
            }
            self.setMeetingEndEvent();

            MeetingORM.update({
              where: self.meeting.id,
              data: {
                status: self.meetingStatuses.completed,
              },
            })
              .then(meeting => {
                MeetingMW.PUT(meeting, false).catch(() => {
                  self.$swal(self.alertError());
                });
              })
              .catch(() => {
                self.$swal(self.alertError());
              });
            this.$store.state.showLoaderOverlay = false;
          }
        })
        .catch(err => {
          console.log("err", err);
          self.$swal(self.alertError());
        });
    },
    revote() {
      // TODO: Not exactly know what will happen if we revote with only mandates from minutes ???:
      var self = this;
      self
        .$swal(self.alertQuestion())
        .then(resQuest => {
          if (resQuest.value) {
            MeetingResolutionORM.update({
              where: self.currentResolution.id,
              data(item) {
                item.status = null;
                item.revote_majority_id = self.majorityConditions.revoteMajority;
              },
            }).then(res => {
              MeetingResolutionMW.PUT(res)
                .then(() => {
                  self.setRevoteMajorityIdForResolutionVote(self.currentResolution.id);

                  self.forceRerenderResolution();
                })
                .catch(err => {
                  console.log(err);
                  self.$swal(self.alertError());
                });
            });
          }
        })
        .catch(err => {
          console.log("err", err);
          self.$swal(self.alertError());
        });
    },
    setPresentMandatesFromTable(mandates) {
      this.presentMandatesFromTable = mandates;
    },
    async startCorrectMeetingResolution() {
      this.correctMeetingErrorsIsPressed = true;
      // NOTE: Make sure to set initial majority (revote majority) before start correcting (in case of revote)
      this.setInitialMeetingResolutionBeforeUpdate();
      let currentResolution = MeetingResolutionORM.find(this.currentResolutionId);
      if (currentResolution) {
        currentResolution.status = this.meetingResolutionStatuses.corrected;
        await MeetingResolutionMW.PUT(currentResolution).catch(err => {
          console.error("mr status was not updated");
        });
        // NOTE: Display desired first majority (in case was choosed by user)
        this.setDesiredMeetingResolutionAfterUpdate();
      } else {
        console.warn("MResolution was not found");
      }
    },
    setInitialMeetingResolutionBeforeUpdate() {
      // NOTE: old_revote_majority_id exists because user selected first majority, so we set meetingResolution back to revote majority before DB update
      if (this.currentResolution.old_revote_majority_id) {
        MeetingResolutionORM.update({
          where: this.currentResolution.id,
          data: {
            revote_majority_id: this.currentResolution.old_revote_majority_id,
          },
        });
      }
    },
    setDesiredMeetingResolutionAfterUpdate() {
      // NOTE: we set meetingResolution back to desired first majority after DB update
      if (this.currentResolution.old_revote_majority_id) {
        MeetingResolutionORM.update({
          where: this.currentResolution.id,
          data: {
            revote_majority_id: null,
          },
        });
      }
    },
    endCorrectMeetingResolution() {
      this.correctMeetingErrorsIsPressed = false;
      this.closeResolution();
    },
    findObjectInArrayById(array, id) {
      var toReturn = null;
      for (let item of array) {
        if (item.id == id) {
          toReturn = item;
          return toReturn;
        }
      }
      return toReturn;
    },

    forceRerenderResolution() {
      this.resolutionKey++;
    },
    getPdfConfigs() {
      // Get current pdf_configs value
      if (this.configuration && this.configuration.settings.length > 0) {
        this.configuration.settings.forEach(element => {
          if (element.pdf_footer) {
            this.pdfFooterMessage = element.pdf_footer;
          }
        });
      }
    },
    resetMeeting() {
      var self = this;
      try {
        self.$swal(self.alertQuestion()).then(response => {
          if (response.value) {
            if (!self.checkIfPvSent) {
              self.$store.state.showLoaderOverlay = true;
              MeetingMW.RESET(self.meeting).then(() => {
                self.$store.state.showLoaderOverlay = false;

                self.$router.push({ name: "meetings" });
              });
            }
          }
        });
      } catch (err) {
        self.$store.state.showLoaderOverlay = false;
        console.error(err);
        self.$swal(self.alertError());
      }
    },
    async createResolutionVoteOnMajorChanges(meetingId) {
      await this.updateResolutionVoteEvent(meetingId);
      await this.createResolutionMajorChangeEvent(meetingId);
    },
    async updateResolutionVoteEvent(meetingId) {
      let meeting = MeetingORM.find(meetingId);
      let indexOfResolutionVote = this.$_.findIndex(meeting.minutes, minuteEvent => {
        return (
          minuteEvent.event == meetingMinutesNames.resolution_vote &&
          minuteEvent.resolution_id == this.currentResolutionId
        );
      });
      if (indexOfResolutionVote != -1) {
        let votes = this.getVotesForCurrentResolution(
          meeting.minutes[indexOfResolutionVote].resolution_id
        );
        meeting.minutes[indexOfResolutionVote].votes = votes;

        MeetingORM.update({
          where: meeting.id,
          data: {
            minutes: meeting.minutes,
          },
        });
        await MeetingMW.PUT(meeting)
          .then(res => {
            this.$store.state.showLoaderOverlay = false;
          })
          .catch(err => {
            this.$store.state.showLoaderOverlay = false;
            console.log("err", err);
          });
      }
    },
    comparer(otherArray) {
      return function (current) {
        return (
          otherArray.filter(function (other) {
            return other.id == current.id;
          }).length == 0
        );
      };
    },
    GENERATE_EXCEL() {
      this.excelSheets = this.getExcelSheetsData();

      setTimeout(() => {
        this.$refs["xlsx-download-button"].click();
      }, 0);
    },
    getExcelSheetsData() {
      var sheetName = this.meeting.name;
      // NOTE: "Error: Sheet names cannot exceed 31 chars"
      sheetName = sheetName.substring(0, 28) + "...";
      var meetingData = [];

      var resolutionVoteEvents = this.meeting.minutes.filter(
        event => event.event === meetingMinutesNames.resolution_vote
      );

      var voteOrder = 1;
      for (const resolutionVoteEvent of resolutionVoteEvents) {
        var resolution = MeetingResolutionORM.find(resolutionVoteEvent.resolution_id);

        var majority = this.findMajorityData(resolutionVoteEvent.majority_id);
        var revoteMajority = this.findMajorityData(resolutionVoteEvent.revote_majority_id);

        var votingCategory = this.findVotingCategoryData(resolution.voting_category_id);

        var parentResolution = null;
        var parentResolutionNumber = "";
        if (resolutionVoteEvent.parent_resolution_id) {
          parentResolution = MeetingResolutionORM.find(resolutionVoteEvent.parent_resolution_id);
          parentResolutionNumber = parentResolution.resolution_number + ".";
        }

        var voteResult = "";
        if (revoteMajority) {
          voteResult = "Revotée";
        } else {
          switch (resolution.status) {
            case this.meetingResolutionStatuses.accepted:
              voteResult = "Acceptée";
              break;
            case this.meetingResolutionStatuses.rejected:
              voteResult = "Rejetée";
              break;
            case this.meetingResolutionStatuses.not_resolved:
              voteResult = "Non résolu";
              break;
            case this.meetingResolutionStatuses.sans_vote:
              voteResult = "Sans vote";
              break;
            case this.meetingResolutionStatuses.rejected_lack_of_majority:
              voteResult = "Rejetée faute de majorité";
              break;
            default:
              break;
          }
        }

        meetingData.push({
          "Ordre de vote": voteOrder,
          "Résolution n°": parentResolutionNumber + resolution.resolution_number,
          "Nom de la résolution": parentResolution ? parentResolution.name : resolution.name,
          "Nom de la sous résolution": parentResolution ? resolution.name : "",
          Majorité: majority ? majority.name : "",
          "Majorité revotée": revoteMajority ? revoteMajority.name : "",
          "Clé de vote": votingCategory ? votingCategory.name : "",
          "Résultat du vote": voteResult,
        });

        voteOrder++;
      }

      return [{ name: sheetName, data: meetingData }];
    },

    async doVoteAllNotVoted() {
      try {
        this.voteEvents = [];
        for (var resolution of this.allMeetingResolutions) {
          if (resolution.children_resolutions.length > 0) {
            for (var childResolution of resolution.children_resolutions) {
              if (!childResolution.status) {
                await this.oneResolutionNotVotedProcess(childResolution);
              }
            }
          } else if (!resolution.parent_resolution_id && !resolution.status) {
            await this.oneResolutionNotVotedProcess(resolution);
          }
        }
        await this.updateMeetingMinutesVotes(this.voteEvents);
        this.forceRerenderResolution();
      } catch (err) {
        console.error(err);
        this.$swal(this.alertError());
      }
    },
    async oneResolutionNotVotedProcess(resolution) {
      let majority = this.getResolutionMajority(resolution);
      let status = this.meetingResolutionStatuses.not_voted;
      let votes = this.getCurrentResolutionVotes(resolution, status);

      if (this.isRevoteCase(resolution, majority, status)) {
        // set resolution revote_majority_id then ..
        await MeetingResolutionORM.update({
          where: resolution.id,
          data(item) {
            item.revote_majority_id = majority.majority_conditions.revoteMajority;
          },
        }).then(async mr => {
          let meetingResolution = this.$_.find(this.allMeetingResolutions, mr2 => {
            return mr2.id == mr.id;
          });
          this.createVoteEvent(meetingResolution, majority, votes, true);
          await this.oneResolutionNotVotedProcess(meetingResolution);
        });
      } else {
        this.createVoteEvent(resolution, majority, votes, false);
      }
    },
    attachTooltip(e, data) {
      const TooltipClass = Vue.extend(AppTooltip);
      const tooltipInstance = new TooltipClass({
        propsData: {
          event: data,
          id: e.target.id,
          placement: "bottom",
        },
      });
      tooltipInstance.$mount(e.target.parent);
      tooltipInstance.$on("change", changeEvent => {
        this.changeMinuteEventDate(changeEvent.newDate, changeEvent.minuteEvent).then(() => {
          tooltipInstance.$destroy();
        });
      });
      tooltipInstance.$on("destroy-tooltip", () => {
        tooltipInstance.$destroy();
      });
    },
    toUtc(date) {
      /*
       * This function will make sure that old meeting data that has no timezone will have UTC
       */
      if (date) {
        return this.$moment.parseZone(date).utcOffset(0, true).format("YYYY-MM-DDTHH:mm:ssZ");
      } else {
        return date;
      }
    },
    getMandatesUpdatedFromMinutes() {
      const resolutionMinuteEvent = this.meeting.minutes.find(
        item =>
          item.event == meetingMinutesNames.resolution_vote &&
          item.resolution_id == this.currentResolutionId
      );

      if (this.presenceTableShowMinutesData && this.currentResolutionId && resolutionMinuteEvent) {
        // NOTE: Get all minutes events before currentResolution was voted
        const minutesMandatesEvents = this.getMinutesMandatesForResolution(
          this.currentResolutionId
        );
        return this.getMandatesWithDataFromMinutes(minutesMandatesEvents);
      } else {
        return MandateORM.query().where("meeting_id", this.meeting.id).get();
      }
    },
    getMandatesWithDataFromMinutes(minutesEvents) {
      let ormMandates = MandateORM.query().where("meeting_id", this.meeting.id).get();
      const mandates = [];
      for (const mandate of ormMandates) {
        const mandateMinuteEvent = this.getMandateLastUpdateFromMinutes(minutesEvents, mandate.id);
        if (mandateMinuteEvent && mandateMinuteEvent.new_presence_status) {
          let clonedMandate = { ...mandate };
          clonedMandate.presence_status = mandateMinuteEvent.new_presence_status;
          mandates.push(clonedMandate);
        }
      }
      return mandates;
    },
    setMeetingResolutionStatusNull(currentResolutionId) {
      MeetingResolutionORM.update({
        where: currentResolutionId,
        data: { status: null },
      });
      // update DB value also
      const currentResolution = MeetingResolutionORM.find(currentResolutionId);
      MeetingResolutionMW.PUT(currentResolution);
    },
    changedCurrentResolutionMajorityId(majorityId) {
      // NOTE: revote_majority_id or old_revote_majority_id has to be set
      if (
        this.meetingIsCompleted &&
        !this.correctMeetingErrorsIsPressed &&
        (this.currentResolution.revote_majority_id || this.currentResolution.old_revote_majority_id)
      ) {
        // NOTE: If revote_majority_id & majorityId is different than revote_majority_id, it means that user want to see votes for primary majority
        if (
          this.currentResolution.revote_majority_id &&
          majorityId != this.currentResolution.revote_majority_id
        ) {
          MeetingResolutionORM.update({
            where: this.currentResolutionId,
            data: {
              majority_id: majorityId,
              revote_majority_id: null,
              // NOTE: Store revote_majority_id for reverting the values
              old_revote_majority_id: this.currentResolution.revote_majority_id,
            },
          });
        } else if (
          !this.currentResolution.revote_majority_id &&
          majorityId == this.currentResolution.old_revote_majority_id
        ) {
          // NOTE: If revote_majority_id is null and majorityId is equal to old_revote_majority_id, it means that user want to see votes for revote majority
          MeetingResolutionORM.update({
            where: this.currentResolutionId,
            data: {
              revote_majority_id: majorityId,
              // NOTE: Reset revote_majority_id
              old_revote_majority_id: null,
            },
          });
        } else {
          return;
        }

        // NOTE: Update ResolutionVotes from store
        this.updateResolutionVotesFromMinutes(this.currentResolutionId, majorityId);
      }
    },
  },
};
</script>
